import $ from 'jquery';
import { getXPathForElement } from '../../util/dom.js';
// import mpro from './mpro.init';

/**
 * @typedef {{ serverId: string|null; name: string; size: number; type: string; metadata: any; }} LSFile
 */

// Cached filepond object
/** @type {import('filepond')} */
let FilePond;
/**
 * Cached locales (en is loaded by default)
 */
const localesLoaded = { 'en-en': true };
const STORAGE_KEY = 'ajaxUploaderLimbo';

/**
 * Load the FilePond instance
 */
export const loadFilePond = async () => {

	/** @type {import('filepond')} */
	// @ts-ignore
	FilePond = await import('filepond/dist/filepond.esm.js');
	// FilePond = await import('../plugins/filepond.esm.js');

	// Load locales and plugins
	const [
		{ 'default': FilePondPluginImageExifOrientation },
		{ 'default': FilePondPluginFileValidateSize },
		{ 'default': FilePondPluginFileValidateType },
		{ 'default': FilePondPluginImageValidateSize },
		// { default: FilePondPluginFileEncode },
		// { default: FilePondPluginFileMetadata },
		{ 'default': FilePondPluginImagePreview },
	] = await Promise.all([
		import('filepond-plugin-image-exif-orientation'),
		import('filepond-plugin-file-validate-size'),
		import('../plugins/filepond-plugin-file-validate-type.esm.js'),
		import('filepond-plugin-image-validate-size'),
		// import('filepond-plugin-file-encode'),
		// import('filepond-plugin-file-metadata'),
		import('filepond-plugin-image-preview'),
		loadLocaleMessages(mpro.getCurrentLocale(), FilePond),
	]);

	// Register plugins
	FilePond.registerPlugin(
		FilePondPluginImageExifOrientation,
		FilePondPluginFileValidateSize,
		FilePondPluginFileValidateType,
		FilePondPluginImageValidateSize,
		// FilePondPluginFileEncode,
		// FilePondPluginFileMetadata,
		FilePondPluginImagePreview,
		// FilePondPluginImageCrop,
		// FilePondPluginImageResize,
		// FilePondPluginImageTransform,
		// FilePondPluginImageEdit
	);
};

const isObject = value => typeof value === 'object' && value !== null;
// const isString = value => typeof value === 'string';
const isNumber = value => typeof value === 'number';
const isInt = value => isNumber(value) && isFinite(value) && Math.floor(value) === value;
const isGet = method => /GET|HEAD/.test(method);

/**
 * Custom xhr api for FilePOnd
 * @param {any} data
 * @param {string} url
 * @param {any} options
 */
const ajaxUploadRequest = (data, url, options) => {

	const api = {
		onheaders: (xhr) => {},
		onprogress: (endlessMode, loadedSize, totalSize) => {},
		onload: (xhr, e) => {},
		ontimeout: (xhr, e) => {},
		onerror: (xhr, e) => {},
		onabort: (xhr) => {},
		abort: () => {
			aborted = true;
			xhr.abort();
		},
	};

	// timeout identifier, only used when timeout is defined
	let aborted = false;
	let headersReceived = false;

	// set default options
	options = {
		method: 'POST',
		timeout: 60_000,
		headers: {
			'x-mp-upload-id': 'ajax_upload',
		},
		withCredentials: false,
		...options,
	};

	// encode url
	// !! Don't double encode the url
	// url = encodeURI(url);

	// if method is GET, add any received data to url

	if (isGet(options.method) && data) {
		url = `${url}${encodeURIComponent(typeof data === 'string' ? data : JSON.stringify(data))}`;
	}

	// create request
	const xhr = new XMLHttpRequest();

	// progress of load
	const process = isGet(options.method) ? xhr : xhr.upload;
	process.onprogress = (e) => {
		// no progress event when aborted ( onprogress is called once after abort() )
		if (aborted) {
			return;
		}

		api.onprogress(e.lengthComputable, e.loaded, e.total);
	};

	// tries to get header info to the app as fast as possible
	xhr.onreadystatechange = () => {
		// not interesting in these states ('unsent' and 'openend' as they don't give us any additional info)
		if (xhr.readyState < 2) {
			return;
		}

		// no server response
		if (xhr.readyState === 4 && xhr.status === 0) {
			return;
		}

		if (headersReceived) {
			return;
		}

		headersReceived = true;

		// we've probably received some useful data in response headers
		api.onheaders(xhr);
	};

	// load successful
	xhr.onload = (e) => {
		// is classified as valid response
		if (xhr.status >= 200 && xhr.status < 300) {
			api.onload(xhr, e);
		}
		else {
			api.onerror(xhr, e);
		}
	};

	// error during load
	xhr.onerror = e => api.onerror(xhr, e);

	// request aborted
	xhr.onabort = () => {
		aborted = true;
		api.onabort();
	};

	// request timeout
	xhr.ontimeout = e => api.ontimeout(xhr, e);

	// open up open up!
	xhr.open(options.method, url, true);

	// set timeout if defined (do it after open so IE11 plays ball)
	if (isInt(options.timeout)) {
		xhr.timeout = options.timeout;
	}

	// add headers
	for (const key of Object.keys(options.headers)) {
		const value = unescape(encodeURIComponent(options.headers[key]));
		xhr.setRequestHeader(key, value);
	}

	// set type of response
	if (options.responseType) {
		xhr.responseType = options.responseType;
	}

	// set credentials
	if (options.withCredentials) {
		xhr.withCredentials = true;
	}

	// let's send our data
	xhr.send(data);

	return api;
};

/**
 * @param {string} header
 */
const getTransferIdFromHeader = (header) => {
	if (/x-content-transfer-id:/i.test(header)) {
		const id = (header.split(':')[1] || '').trim();
		return id || null;
	}
	return null;
};

/**
 * @param {string} header
 */
const getFileNameFromHeader = (header) => {
	// test if is content disposition header, if not exit
	if (!/^content-disposition:/i.test(header)) {
		return null;
	}

	// get filename parts
	const matches = header
		.split(/filename=|filename\*=.+''/)
		.splice(1)
		.map(name => name.trim().replaceAll(/^["']|["';]{0,2}$/g, ''))
		.filter(name => name.length);

	return matches.length > 0 ? decodeURI(matches.at(-1)) : null;
};

/**
 * @param {string} header
 */
const getFileSizeFromHeader = (header) => {
	if (/content-length:/i.test(header)) {
		const size = header.match(/\d+/)[0];
		return size ? Number.parseInt(size, 10) : null;
	}
	return null;
};

/**
 * @param {string} headers
 */
const getFileInfoFromHeaders = (headers) => {
	const info = {
		source: null,
		name: null,
		size: null,
	};

	const rows = headers.split('\n');
	for (const header of rows) {
		const name = getFileNameFromHeader(header);
		if (name) {
			info.name = name;
			continue;
		}

		const size = getFileSizeFromHeader(header);
		if (size) {
			info.size = size;
			continue;
		}

		const source = getTransferIdFromHeader(header);
		if (source) {
			info.source = source;
			continue;
		}
	}

	return info;
};

/**
 * @param {string} url
 */
const getFilenameFromURL = url =>
	url
		.split('/')
		.pop()
		.split('?').shift();

/**
 * @param {File} blob
 * @param {string} filename
 */
const getFileFromBlob = (blob, filename) => {

	const file = blob.slice(0, blob.size, blob.type);

	// @ts-ignore
	file.lastModifiedDate = new Date();
	// @ts-ignore
	file.lastModified = file.lastModifiedDate.getTime();

	// Copy relative path
	// @ts-ignore
	if (blob._relativePath) {
		// @ts-ignore
		file._relativePath = blob._relativePath;
	}

	// @ts-ignore
	file.name = filename;

	return file;
};

const createResponse = (type, code, body, headers) => ({
	type,
	code,
	body,
	headers,
});

const createTimeoutResponse = cb => (xhr) => {
	cb(createResponse('error', 0, 'Timeout', xhr.getAllResponseHeaders()));
};

const responseToText = async (xhr) => {
	let text;
	text = xhr.responseType === 'blob' ? (await xhr.response.text()) : xhr.responseText;
	return text;
};

const sendToServer = (type, url, options = {}, onSuccess = null, onError = null) => (...args) => {

	let _transferFn; let _transferOptions; let abort; let error; let fieldName; let file; let headers; let load; let metadata; let payload; let progress; let responseType = 'text';

	if (type === 'process') {

		[fieldName, file, metadata, load, error, progress, abort, _transferFn, _transferOptions] = args;

		// fieldName is the name of the input field
		// file is the actual file object to send
		payload = new FormData();

		// add metadata under same name
		if (isObject(metadata)) {
			payload.append(fieldName, JSON.stringify(metadata));
		}

		// Turn into an array of objects so no matter what the input, we can handle it the same way
		for (const item of (file instanceof Blob ? [{ name: null, file }] : file)) {
			payload.append(
				fieldName,
				item.file,
				item.name === null ? item.file.name : `${item.name}${item.file.name}`,
			);
		}
	}
	else if (type === 'revert') {
		[payload, load, error] = args;
	}
	else {
		[payload, load, error, progress, abort, headers] = args;
		// Expect blob response
		responseType = 'blob';
	}

	// fieldName, file, metadata, load, error, progress, abort, transferFn, transferOptions
	// uniqueFileId, load, error
	// payload, load, error, progress, abort, headers

	// do local or remote request based on if the url is external
	const request = ajaxUploadRequest(payload, url, {
		responseType,
		...options,
	});

	request.onload = async (xhr) => {
		// Get headers
		const responseHeaders = xhr.getAllResponseHeaders();
		let processedResponse;

		// Expect json or text
		if (type === 'process') {
			let serverId;
			try {
				const text = await responseToText(xhr);
				serverId = JSON.parse(text).server_id;
			}
			catch {
				// do nothing
			}
			processedResponse = serverId == null ? xhr.responseText : serverId;
		}
		else if (type === 'revert') {
			processedResponse = await responseToText(xhr);
		}
		// Expect file object
		else {
			processedResponse = getFileFromBlob(xhr.response, /* filename */ getFileInfoFromHeaders(responseHeaders).name || getFilenameFromURL(payload));
		}

		// Build the response object
		const response = createResponse(
			'load',
			xhr.status,
			processedResponse,
			responseHeaders,
		);
		// Create response
		load(response);
		// Call custom callback
		onSuccess && onSuccess(response, payload);
	};

	request.onerror = async (xhr) => {
		let text;
		try {
			text = JSON.parse(await responseToText(xhr)).error.message;
		}
		catch {
			// do nothing
		}

		const errorResponse = createResponse(
			'error',
			xhr.status,
			text || xhr.statusText,
			xhr.getAllResponseHeaders(),
		);

		error(errorResponse);
		onError && onError(errorResponse, payload);
	};

	if (headers) {
		request.onheaders = (xhr) => {
			headers(xhr.getAllResponseHeaders());
		};
	}

	request.ontimeout = createTimeoutResponse(error);
	if (progress) {
		request.onprogress = progress;
	}
	if (abort) {
		request.onabort = abort;
	}

	// should return request
	return request;
};

/**
 * Load locale
 *
 * @param {string} langLocale
 * @param {import('filepond')} Filepond
 */
export const loadLocaleMessages = async (langLocale, Filepond) => {

	// this will bundle all locales into a single file /* webpackMode: "eager" */

	// Get list of current locales
	const locales = mpro.getCurrentLocalesFor(langLocale, 'filepond');
	const localesToLoad = [];

	// Load locales only once
	for (const locale of locales) {
		if (!localesLoaded[locale]) {
			localesToLoad.push(locale);
			localesLoaded[locale] = true;
		}
	}

	if (localesToLoad.length > 0) {
		await Promise.allSettled(localesToLoad.map((locale) => {
			return import(
				/* webpackChunkName: "filepond-i18n/[request]" */
				`filepond/locale/${locale}.js`
			).then(locale => void Filepond.setOptions(locale));
		}));
	}
};

const lsKey = instanceId => (STORAGE_KEY + ':' + instanceId);

/**
 * @param {import('filepond').FilePondFile} file
 *
 * @return {LSFile}
 */
const getFileData = (file) => {
	return {
		serverId: file.serverId,
		name: file.filename,
		size: file.fileSize,
		type: file.fileType,
		metadata: file.getMetadata(),
	};
};

/**
 * Retrieves files saved in sessionStorage for the current uploader session
 *
 * @param {string} instanceId
 */
export const getLocalFiles = (instanceId) => {

	const item = sessionStorage.getItem(lsKey(instanceId));

	if (item) {
		try {
			/** @type {LSFile[]} */
			const filesList = JSON.parse(item);
			if (Array.isArray(filesList)) {
				return filesList;
			}
		}
		catch {
			// continue
		}
	}
	return [];
};

/**
 * Delete a fileId from sessionStorage
 *
 * @param {string} instanceId
 * @param {string} serverId
 */
export const getLocalFile = (instanceId, serverId) => {
	// Update the files list
	const filesList = getLocalFiles(instanceId);
	return filesList.find(item => (serverId === item.serverId));
};

/**
 * Save a fileId to sessionStorage in case the page is closed/refreshed and the temp file is still on the server
 *
 * @param {string} instanceId
 * @param {import('filepond').FilePondFile} file
 */
export const saveLocalFile = (instanceId, file) => {
	// Update the files list
	const filesList = getLocalFiles(instanceId);
	filesList.push(getFileData(file));
	sessionStorage.setItem(lsKey(instanceId), JSON.stringify(filesList));
};

/**
 * Delete a fileId from sessionStorage
 *
 * @param {string} instanceId
 * @param {import('filepond').FilePondFile|LSFile} file
 */
export const removeLocalFile = (instanceId, file) => {
	// Update the files list
	const filesList = getLocalFiles(instanceId);
	const index = filesList.findIndex(item => (file.serverId === item.serverId));
	if (index !== -1) {
		filesList.splice(index, 1);
	}
	sessionStorage.setItem(lsKey(instanceId), JSON.stringify(filesList));
};

/**
 * Clear files from sessionStorage
 *
 * @param {string} instanceId
 */
export const clearLocalFiles = (instanceId) => {
	sessionStorage.removeItem(lsKey(instanceId));
};

/**
 * @param {JQuery<HTMLInputElement>} $fileInputs
 */
export const initAjaxUploadDecorator = async ($fileInputs) => {

	for (const fileInput of $fileInputs) {
		await decorateAjaxUploadInput(fileInput, $(fileInput).data('ajax-upload') || {});
	}
};

/**
 *
 * @param {HTMLInputElement} fileInput
 * @param {*} options
 *
 * @returns {Promise<InstanceType<FilePond>>}
 */
export const decorateAjaxUploadInput = async (fileInput, options = {}) => {

	const $fileInput = $(fileInput);

	if ($fileInput.data('filepondInstance')) {
		return $fileInput.data('filepondInstance');
	}

	// First load
	if (!FilePond) {
		await loadFilePond();
	}

	const instanceId = getXPathForElement(fileInput);

	/** @type {import('filepond').FilePondOptions} */
	const fpOptions = {};

	// Copy only allowed props
	for (const opt of ['maxFiles', 'maxFileSize', 'labelIdle', 'allowImagePreview', 'restrictedExtensions', 'beforeRemoveFile']) {
		if (options[opt] != null) {
			fpOptions[opt] = options[opt];
		}
	}

	// Convert to bytes
	if (fpOptions.maxFileSize) {
		// @ts-ignore
		fpOptions.maxFileSize = mpro.humanReadableToBytes(fpOptions.maxFileSize);
	}

	const $form = $(fileInput.form);
	const submitData = [];
	const ajaxName = fileInput.name;

	let {
		submitName = ajaxName,
		urlUploadTemp = mpro.config.uploader.url_upload_temp,
		urlDeleteTemp = mpro.config.uploader.url_delete_temp,
		urlGetTemp = mpro.config.uploader.url_get_temp,
		urlDownloadTemp = mpro.config.uploader.url_download_temp,
		typesMap = {},
		files: filesData,
	} = options;

	const allowFileTypeValidation = true; // fileInput.accept ? true : false;
	const acceptedFileTypes = fileInput.accept ? fileInput.accept.split(',').map(v => v.trim()) : [];

	// Correct object type (usually json_encode does this to empty php arrays)
	if (!typesMap || Array.isArray(typesMap)) {
		typesMap = {};
	}

	/** @type {import('filepond').FilePondInitialFile[]} */
	let files;

	// Got server files
	if (Array.isArray(filesData) && filesData.length > 0) {
		// TODO: populate file from server
	}
	else {
		files = [];

		// Populate the instance with files in limbo
		for (const file of getLocalFiles(instanceId)) {
			files.push({
				// the server file reference
				source: file.serverId,
				// set type to limbo to tell FilePond this is a temp file
				options: {
					type: 'limbo',
					// Note: don't set the file/metadata info in order for filepond to download it using the urlDownloadTemp request
					// file: {
					// 	name: file.name,
					// 	size: file.size,
					// 	type: file.type,
					// },
					// metadata: file.metadata,
				},
			});
		}
	}

	const serverOptions = {
		timeout: 60_000,
		headers: {
			'x-mp-upload-id': 'ajax_upload',
		},
	};

	// labelIdle: 'Drag &amp; Drop your picture or <span class="filepond--label-action">Browse</span>'

	// Add the configured file temp upload name
	if (!fileInput.name) {
		fileInput.name = mpro.config.uploader.ajax_name;
	}

	// !! VERY IMPORTANT !!
	// !! DO NOT SET 'integrated' when the uploader is in 'multiple' mode !!
	// https://github.com/pqina/filepond/issues/389
	const stylePanelLayout = fileInput.multiple ? null : 'integrated';

	const fp = FilePond.create(fileInput, {

		// name: fileInput.name,

		// @ts-ignore
		credits: false,

		// allowDrop: true,
		// allowBrowse: true,
		// allowPaste: true,
		// allowMultiple: false,
		// allowReplace: true,
		// allowRevert: true,
		// allowRemove: true,
		// allowProcess: true,
		// allowReorder: false,
		// storeAsFile: false,
		// dropOnPage: false,
		// dropOnElement: true,
		dropValidation: true,
		// ignoredFiles: ['.ds_store', 'thumbs.db', 'desktop.ini'],
		// forceRevert: false,
		maxFiles: null,
		// maxParallelUploads: 2,
		itemInsertLocation: 'after',
		// itemInsertInterval: 75,
		checkValidity: true,
		labelFileLoadError(/** @type {{ body: string; code: number; headers: string; type: string; }} */ serverError) {
			return serverError.body || 'Error during load';
		},
		labelFileProcessingError(/** @type {{ body: string; code: number; headers: string; type: string; }} */ serverError) {
			return serverError.body || 'Error during upload';
		},
		labelFileProcessingRevertError(/** @type {{ body: string; code: number; headers: string; type: string; }} */ serverError) {
			return serverError.body || 'Error during revert';
		},
		labelFileRemoveError(/** @type {{ body: string; code: number; headers: string; type: string; }} */ serverError) {
			return serverError.body || 'Error during remove';
		},

		stylePanelLayout,
		// stylePanelAspectRatio: null,

		// FilePondPluginImageExifOrientation options
		allowImageExifOrientation: true,

		// FilePondPluginFileValidateSize options
		allowFileSizeValidation: true,
		minFileSize: null,
		maxFileSize: null, // '5MB'
		maxTotalFileSize: null,
		labelMaxFileSizeExceeded: mpro.langvar('error_file_too_large') || 'File is too large',
		labelMaxFileSize: mpro.langvar('error_max_filesize', { filesize: '{filesize}' }) || 'Maximum file size is {filesize}',
		labelMaxTotalFileSizeExceeded: mpro.langvar('error_filesize_exceeded') || 'Maximum total size exceeded',
		labelMaxTotalFileSize: mpro.langvar('error_filesize_value', { filesize: '{filesize}' }) || 'Maximum total file size is {filesize}',

		// FilePondPluginFileValidateType options
		allowFileTypeValidation,
		acceptedFileTypes,
		restrictedExtensions: null,
		labelFileTypeNotAllowed: mpro.langvar('error_invalid_file_extension') || 'File of invalid type',
		fileValidateTypeLabelExpectedTypes: mpro.langvar('error_allowed_extensions', { allowed_types: '{allTypes}' }) || 'Expects: {allTypes}',
		fileValidateTypeLabelExpectedTypesMap: typesMap,
		fileValidateTypeDetectType: null,

		// FilePondPluginImageValidateSize options
		allowImageValidateSize: true,
		imageValidateSizeMinWidth: 1,
		imageValidateSizeMaxWidth: 65_535,
		imageValidateSizeMinHeight: 1,
		imageValidateSizeMaxHeight: 65_535,
		imageValidateSizeLabelFormatError: mpro.langvar('error_invalid_image_type') || 'Image type not supported',
		imageValidateSizeLabelImageSizeTooSmall: mpro.langvar('error_image_too_small') || 'Image is too small',
		imageValidateSizeLabelImageSizeTooBig: mpro.langvar('error_image_too_big') || 'Image is too big',
		imageValidateSizeLabelExpectedMinSize: mpro.langvar('error_min_image_dimension', { min: '{minWidth}', max: '{minHeight}' }) || 'Minimum size is {minWidth} × {minHeight}',
		imageValidateSizeLabelExpectedMaxSize: mpro.langvar('error_max_image_dimension', { min: '{minWidth}', max: '{minHeight}' }) || 'Maximum size is {maxWidth} × {maxHeight}',
		imageValidateSizeMinResolution: null,
		imageValidateSizeMaxResolution: null,
		imageValidateSizeLabelImageResolutionTooLow: mpro.langvar('error_image_resolution_low') || 'Resolution is too low',
		imageValidateSizeLabelImageResolutionTooHigh: mpro.langvar('error_image_resolution_high') || 'Resolution is too high',
		imageValidateSizeLabelExpectedMinResolution: mpro.langvar('error_min_image_resolution', { min: '{minResolution}' }) || 'Minimum resolution is {minResolution}',
		imageValidateSizeLabelExpectedMaxResolution: mpro.langvar('error_max_image_resolution', { max: '{maxResolution}' }) || 'Maximum resolution is {maxResolution}',
		imageValidateSizeMeasure: null,

		// FilePondPluginFileEncode options
		// allowFileEncode: true,

		// FilePondPluginFileMetadata options
		// allowFileMetadata: true,
		// fileMetadataObject: null,

		// FilePondPluginImagePreview options
		allowImagePreview: false,
		imagePreviewMinHeight: 44,
		imagePreviewMaxHeight: 120,
		imagePreviewHeight: null,
		imagePreviewMaxFileSize: null,
		imagePreviewTransparencyIndicator: null,
		imagePreviewMaxInstantPreviewFileSize: 1_000_000,
		imagePreviewMarkupShow: true,
		imagePreviewMarkupFilter: markupItem => true,
		imagePreviewFilterItem: fileItem => true,

		// Populate with files
		files,

		// Endpoints config
		server: {

			...serverOptions,

			// [POST] Upload temp file to server
			// process: (fieldName, file, metadata, load, error, progress, abort, transfer, options) => {
			// 	load(mpro.uniqId());
			// 	return { abort };
			// },
			process: sendToServer('process', urlUploadTemp, { ...serverOptions, method: 'POST' }),

			// [DELETE] Delete temp file from server
			// revert: urlDeleteTemp,
			revert: sendToServer('revert', urlDeleteTemp, { ...serverOptions, method: 'DELETE' }),

			// [GET] Retrieve temp file from server (was the original signature request)
			// [POST] Restore temp file from server (from sessionStorage id)
			restore: sendToServer('restore', urlGetTemp, serverOptions, null, (error, payload) => {

				// File not available anymore on the server - remove it from storage
				const localFile = getLocalFile(instanceId, payload);
				if (localFile) {
					removeLocalFile(instanceId, localFile);
				}

				// Auto-remove the file from the queue
				for (const file of fp.getFiles()) {
					if (file.serverId === payload) {
						fp.removeFile(file);
						break;
					}
				}
			}),

			// [GET | HEAD] Fetch file from remote url source
			// fetch: '?action=file_fetch'
			// fetch: urlDownloadTemp + '&file=',
			fetch: sendToServer('fetch', urlDownloadTemp, serverOptions),

			// [GET] Load/Populate file from server (NOT temp) (was the original signature request)
			// [POST] Load/Populate file from server (NOT temp)
			// load: sendToServer(urlLoad, serverOptions),

			// [POST] Delete db file from server (NOT temp)
			// remove: sendToServer(urlDelete, serverOptions),
		},

		...fpOptions,

		onaddfile: (error, file) => {
			// FilePond sets the file.origin to LIMBO for added from the fetch endpoint
			if ((typeof file.source === 'string') && (/^(https?|ftps?)/i.test(file.source))) {
				// Set the file.origin back to INPUT so we can save it in sessionStorage
				file.origin = FilePond.FileOrigin.INPUT;
			}
		},

		onprocessfile: (error, file) => {
			if (!error) {
				// All ok
				if (file.serverId) {
					submitData.push(file.serverId);
					handleSubmitData();

					// Save only files that were added in this browser session
					if (file.origin === FilePond.FileOrigin.INPUT) {
						saveLocalFile(instanceId, file);
					}
				}
				// Invalid JSON in process.onload handler
				else {
					fp.removeFile(file);
				}
			}
		},

		onprocessfilerevert: (file) => {
			if (file.serverId) {
				const index = submitData.indexOf(file.serverId);
				if (index !== -1) {
					submitData.splice(index, 1);
				}
				handleSubmitData();
				removeLocalFile(instanceId, file);
			}
		},

		onremovefile: (error, file) => {

			if (!error) {
				if (file.serverId) {
					const index = submitData.indexOf(file.serverId);
					if (index !== -1) {
						submitData.splice(index, 1);
					}
					handleSubmitData();
					removeLocalFile(instanceId, file);
				}
			}
		},
	});

	// Save the instance reference
	$fileInput.data('filepondInstance', fp);
	$(fp.element).data('filepondInstance', fp);

	const handleSubmitData = () => {
		/** @type {JQuery} */
		let $parent;
		const $el = $(fp.element);
		const $obsoleteInput = $el.find(`input[name="${ajaxName}"]`);
		if ($obsoleteInput.length > 0) {
			$parent = $obsoleteInput.parent();
			// Remove the original input
			$obsoleteInput.remove();
		}
		if (!$parent || $parent.length === 0) {
			// @ts-ignore
			$parent = $el;
		}

		let $submitInput = $el.find(`input[name="${submitName}"]`);
		if ($submitInput.length === 0) {
			$submitInput = $(`<input type="hidden" name="${submitName}" />`);
			$submitInput.appendTo($parent);
		}
		$submitInput.val(JSON.stringify(submitData));
	};

	// Run the handler on first init
	handleSubmitData();

	// Prevent form submission when files are being uploaded to the server
	if ($form.length > 0) {
		$form.on('submit.fp-handle-upload', (e) => {
			// Upload in progress
			if (fp.status === FilePond.Status.BUSY) {
				mpro.toast('warning', 'Please wait until the file upload is complete!', { positionX: 'center' });
				e.stopImmediatePropagation();
				e.stopPropagation();
				e.preventDefault();
			}
			// Clear the local files
			else {
				clearLocalFiles(instanceId);
			}
		});
	}

	return fp;
};

export default mpro;